import React, { useEffect, useRef, useState } from 'react'

import update from 'react-addons-update'
import { useToasts } from 'react-toast-notifications'
import styled from 'styled-components'

import { ResponsivePXValue } from '@client/components'
import { ErrorBlock, ModalLoading, ModalFormContainer } from '@client/components/molecules'
import { useConfig } from '@client/contexts/ConfigProvider'
import { useEvents } from '@contexts/GTMProvider'
import {
  useUserDetailsQuery, useUserCartQuery, UserDetailsDocument, UserCartDocument,
  SubscribeToPaymentChangeDocument, useCheckPaymentStatusLazyQuery, useAddAndValidateCreditCardMutation,
  useRemoveCreditCardMutation, RegisteredUserDetailsFragment, UserDetailsFragment,
} from '@hooks/api'
import { useProductGroups } from '@hooks/UseProductGroups'
import { AddAndValidateCreditCardInput, TransactionStatusEnum } from '@uctypes/api/globalTypes'

import { AddCardForm } from '../forms/AddCardForm'

enum CreditCardStepEnum {
  ADD_CREDIT_CARD = 'ADD_CREDIT_CARD',
  REDIRECT = 'REDIRECT',
  ERROR = 'ERROR',
  PENDING = 'PENDING',
}

const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
`

const IframeContainer = styled.div`
  width: 100%;
  ${ResponsivePXValue('height', { mobile: '400px', tablet: '400px', desktop: '600px' })}
`

export interface CreditCardDetails {
  cardNickname: string
  nameOnCard: string
  cardNumber: string
  cardType?: string
  expiryMonth: string
  expiryYear: string
  cvv: string
  isDefault: boolean
}

export interface CreditCardFormProps {
  open?: boolean
  onClose: () => void
  onCardSuccess?: (creditCardId: string) => void

  actionButtonText?: string

  secondaryActionButtonText?: string
  className?: string
  checkoutFlowType?: string
}

interface CreditCardFormState {
  displayStep: CreditCardStepEnum
  error: string
  redirect: string
  paymentId: string
  creditCardId: string
}

const DEFAULT_STATE: CreditCardFormState = {
  displayStep: CreditCardStepEnum.ADD_CREDIT_CARD,
  error: '',
  redirect: '',
  paymentId: '',
  creditCardId: '',
}

export function CreditCardForm({ open = true, onClose, onCardSuccess, actionButtonText, secondaryActionButtonText, className, checkoutFlowType }: CreditCardFormProps): JSX.Element {

  const config = useConfig()
  const [state, setState] = useState<CreditCardFormState>({ ...DEFAULT_STATE })
  const { data: userDetailsData, loading: detailsLoading, refetch: refetchDetails } = useUserDetailsQuery({ ssr: config.fetchSSRQuery() })
  const { refetch: refetchCart, loading: cartLoading } = useUserCartQuery({ ssr: config.fetchSSRQuery() })
  const [addAndValidate, { loading: addLoading }] = useAddAndValidateCreditCardMutation()
  const [removeCreditCard, { loading: removeLoading }] = useRemoveCreditCardMutation()
  const [check, { data: statusData, loading: statusLoading, subscribeToMore }] = useCheckPaymentStatusLazyQuery({ variables: { id: state.paymentId } })
  const iFrame: React.RefObject<HTMLIFrameElement> = useRef(null)
  const toasts = useToasts()
  const event = useEvents()
  const user = userDetailsData?.currentUser as UserDetailsFragment & RegisteredUserDetailsFragment
  const { data: userCartData } = useUserCartQuery({ ssr: config.fetchSSRQuery() })
  const currentCategory: string | null = userCartData?.currentUser?.activeMenu?.subscription?.category?.id || null
  const productGroupData = useProductGroups(currentCategory)

  const _handleClose = (): void => {
    if (onClose) {
      onClose()
    }
  }

  const _handleAddCard = async (creditCardDetails: CreditCardDetails): Promise<void> => {
    const successRedirect = ''
    const failureRedirect = ''
    const creditCardInput: AddAndValidateCreditCardInput = {
      cardNickname: creditCardDetails.cardNickname,
      nameOnCard: creditCardDetails.nameOnCard,
      cardNumber: creditCardDetails.cardNumber,
      cvv: creditCardDetails.cvv,
      expiryMonth: creditCardDetails.expiryMonth,
      expiryYear: creditCardDetails.expiryYear,
      user: user.id,
      successRedirect,
      failureRedirect,
      isDefault: true,
    }
    try {
      const response = await addAndValidate({
        variables: {
          input: creditCardInput,
        },
        refetchQueries: [{ query: UserDetailsDocument }, { query: UserCartDocument }],
      })
      setState((prevState) => update(prevState, {
        displayStep: { $set: CreditCardStepEnum.REDIRECT },
        redirect: { $set: response.data.validatedCard.validationUrl },
        creditCardId: { $set: response.data.validatedCard.id },
      }))

    } catch (e) {
      setState((prevState) => ({ ...prevState, displayStep: CreditCardStepEnum.ERROR, error: e.message }))
    }
  }

  const _handleSuccess = async (): Promise<void> => {

    await Promise.all([
      refetchDetails(),
      refetchCart(),
    ])
    onCardSuccess?.(state.creditCardId)

    onClose?.()
    event.hasAddedCreditCardCheckoutStep(productGroupData, userDetailsData?.currentUser?.id, userDetailsData?.currentUser?.email, checkoutFlowType)
    toasts.addToast('Successfully added credit card', {
      appearance: 'success',
      autoDismiss: true,
    })

    await Promise.all([
      refetchDetails(),
      refetchCart(),
    ])
    onCardSuccess?.(state.creditCardId)
    onClose?.()

    await refetchCart()
    await refetchDetails()
  }

  const _handleFailed = async (): Promise<void> => {
    toasts.addToast('Failed to added credit card', {
      appearance: 'error',
      autoDismiss: true,
    })
    await removeCreditCard({
      variables: { id: state.creditCardId },
      refetchQueries: [{ query: UserDetailsDocument }],
      awaitRefetchQueries: true,
    })
    onClose?.()
  }

  const _handlePending = (): () => void => {
    return subscribeToMore({
      document: SubscribeToPaymentChangeDocument,
      variables: { id: state.paymentId },
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev
        const newStatus = subscriptionData.data.status
        return Object.assign({}, prev, {
          status: newStatus,
        })
      },
    })
  }

  useEffect(() => {
    if (open) {
      setState((prevState) => update(prevState, {
        displayStep: { $set: CreditCardStepEnum.ADD_CREDIT_CARD },
      }))
    }
  }, [open])

  useEffect(() => {
    if (state.paymentId) {
      check({ variables: { id: state.paymentId } })
    }
  }, [state.paymentId])

  useEffect(() => {
    let plan: () => void | null = null
    if (statusData?.status?.status) {
      if (statusData?.status?.status === TransactionStatusEnum.FAILED) {
        _handleFailed()
      } else if (statusData?.status?.status === TransactionStatusEnum.SUCCESS) {
        _handleSuccess()
      } else {
        plan = _handlePending()
      }
    }
    return () => plan?.()
  }, [statusData?.status?.status])

  useEffect(() => {
    const _handler = async (event: MessageEvent): Promise<void> => {
      if (config.isBrowser() && event.origin.startsWith(window.location.origin) && event.data.status) {
        if (event.data.id) {
          setState((prevState) => update(prevState, {
            paymentId: { $set: event.data.id },
            displayStep: { $set: CreditCardStepEnum.PENDING },
          }))
        } else {
          setState((prevState) => update(prevState, {
            error: { $set: event.data.error },
            displayStep: { $set: CreditCardStepEnum.ERROR },
          }))
        }

      }
    }
    if (config.isBrowser()) {
      window.addEventListener('message', _handler)
      return () => window.removeEventListener('message', _handler)
    }
  }, [])

  const loading = detailsLoading || addLoading || removeLoading || statusLoading || cartLoading
  let loadingMessage = 'Fetching your information'
  if (addLoading) {
    loadingMessage = 'Attempting to Add Card'
  } else if (removeLoading) {
    loadingMessage = 'Removing failed card'
  } else if (statusLoading) {
    loadingMessage = 'Validating card'
  }

  return (
    <Container className={className}>
      <Choose>
        <When condition={loading}>
          <ModalLoading message={loadingMessage} />
        </When>
        <When condition={state.displayStep === CreditCardStepEnum.PENDING}>
          <ModalLoading message='Please wait' />
        </When>
        <When condition={state.displayStep === CreditCardStepEnum.ERROR}>
          <ErrorBlock
            title={state.error}
            onClick={() => setState((prevState) => ({ ...prevState, displayStep: CreditCardStepEnum.ADD_CREDIT_CARD }))}/>
        </When>
        <When condition={state.displayStep === CreditCardStepEnum.ADD_CREDIT_CARD}>
          <AddCardForm
            onAddCard={_handleAddCard}
            onCancel={_handleClose}
            actionButtonText={actionButtonText}
            secondaryActionButtonText={secondaryActionButtonText} />
        </When>
        <When condition={state.displayStep === CreditCardStepEnum.REDIRECT}>
          <ModalFormContainer >
            <IframeContainer>
              <iframe
                src={state.redirect}
                sandbox='allow-forms allow-scripts allow-same-origin'
                width='100%'
                height='100%'
                ref={iFrame} />
            </IframeContainer>
          </ModalFormContainer>
        </When>
        <Otherwise>
          <AddCardForm
            onAddCard={_handleAddCard}
            onCancel={_handleClose}
            actionButtonText={actionButtonText}
            secondaryActionButtonText={secondaryActionButtonText}/>
        </Otherwise>
      </Choose>
    </Container>
  )
}
